import Immutable, { List } from 'immutable';
import { ProjectGroup } from 'records/ProjectGroup';
import { Markup, MarkupMap } from 'records/Markup';
import { Punch, PunchOrderedMap, PunchStatusValue } from 'records/Punch';
import { PunchQuery } from 'records/PunchQuery';
import { SheetMap } from 'records/Sheet';
import { getShortDateStr } from 'utils/format';
import { getGroupNamesFromIds, getMembersByGroupIds } from 'utils/groups';

/* Filtering Punches */

function filterOverdue(punch: Punch, punchQuery: PunchQuery): boolean {
  if (punchQuery.overdue.size > 0) {
    const comp = punchQuery.overdue.first(0);
    const dueDate = new Date(punch.duedate * 1000);
    if (punch.duedate === 0 || punch.status === PunchStatusValue.CLOSED) {
      return false;
    }
    return dueDate < new Date(comp);
  }

  return true;
}

function filterDisciplines(punch: Punch, punchQuery: PunchQuery): boolean {
  if (punchQuery.disciplines.size > 0) {
    const hasDisciplines = punchQuery.disciplines.some((a) =>
      punch.disciplines.has(a)
    );
    const unassignedChecked = !punchQuery.unassigned.has('disciplines');

    if (punch.disciplines.size > 0 && !hasDisciplines) {
      return false;
    }

    if (!unassignedChecked && punch.disciplines.size === 0) {
      return false;
    }
  }

  if (
    punchQuery.disciplines.size === 0 &&
    !punchQuery.unassigned.has('disciplines')
  ) {
    if (punch.disciplines.size > 0) {
      return false;
    }
  }

  return true;
}

function filterAssignees(
  punch: Punch,
  punchQuery: PunchQuery,
  projectGroups: List<ProjectGroup>
): boolean {
  let hasAssignees = false;
  let hasGroupAssignees = false;
  let isAssignedGroupMember = false;
  let isAssigneeInGroup = false;
  const hasPunchQueryAssignees = punchQuery.assignees.size > 0;
  const hasPunchQueryGroupAssignees = punchQuery.groupAssignees.size > 0;

  const groupMemberList = getMembersByGroupIds(
    projectGroups,
    Array.from(punchQuery.groupAssignees)
  );
  const hasGroupMembers = groupMemberList.size > 0;

  const assignedPunchGroups = getMembersByGroupIds(
    projectGroups,
    Array.from(punch.groupAssignees)
  );

  const shouldFilterBeApplied =
    hasPunchQueryAssignees || hasPunchQueryGroupAssignees || hasGroupMembers;

  if (!shouldFilterBeApplied) {
    return true;
  }
  if (hasPunchQueryAssignees) {
    hasAssignees = punchQuery.assignees.some((assignee) =>
      punch.assignees.has(assignee)
    );
  }

  if (hasPunchQueryGroupAssignees) {
    hasGroupAssignees = punchQuery.groupAssignees.some((assignee) =>
      punch.groupAssignees.has(assignee)
    );
  }

  if (hasGroupMembers) {
    isAssignedGroupMember = groupMemberList.some((assignees) =>
      assignees.some((assignee) => punch.assignees.has(assignee.email))
    );
  }

  if (hasPunchQueryAssignees && assignedPunchGroups.size > 0) {
    isAssigneeInGroup = assignedPunchGroups.some((members) =>
      members.some((member) =>
        punchQuery.assignees.some((assignee) => member.email === assignee)
      )
    );
  }

  return (
    hasAssignees ||
    hasGroupAssignees ||
    isAssignedGroupMember ||
    isAssigneeInGroup
  );
}

function filterUnassigned(punch: Punch, punchQuery: PunchQuery): boolean {
  const unassignedChecked =
    !punchQuery.unassigned.has('assignees') ||
    !punchQuery.unassigned.has('groupAssignees');
  const isUnassignedGroup =
    punchQuery.groupAssignees.size === 0 &&
    !punchQuery.unassigned.has('groupAssignees');
  const isUnassignedIndividual =
    punchQuery.assignees.size === 0 && !punchQuery.unassigned.has('assignees');

  if (unassignedChecked && (isUnassignedGroup || isUnassignedIndividual)) {
    if (punch.groupAssignees.size > 0 || punch.assignees.size > 0) {
      return false;
    }
  }
  return true;
}

function filterStatuses(punch: Punch, punchQuery: PunchQuery): boolean {
  if (punchQuery.statuses.size > 0) {
    return punchQuery.statuses.has(punch.displayString);
  }

  return true;
}

function filterPriorities(punch: Punch, punchQuery: PunchQuery): boolean {
  if (punchQuery.priorities.size > 0) {
    return punchQuery.priorities.has(punch.priority);
  }

  return true;
}

function filterLocations(punch: Punch, punchQuery: PunchQuery): boolean {
  if (punchQuery.locations.size > 0) {
    return punchQuery.locations.has(punch.location.trim());
  }

  return true;
}

function filterSheets(
  punch: Punch,
  sheets: SheetMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  punchQuery: PunchQuery
): boolean {
  // If no pages set, always return true.
  if (punchQuery.pages.size === 0) {
    return true;
  }

  const m = markups.reduce<MarkupMap>(
    (acc, v) => acc.merge(v),
    Immutable.OrderedMap()
  );

  return punch.markupids
    .map((mid) => m.get(mid, new Markup()))
    .filter((markup) => markup.markupid.length > 0)
    .some((mm) => punchQuery.pages.has(mm.fileid));
}

function filterCreatedBy(punch: Punch, punchQuery: PunchQuery) {
  if (punchQuery.createdBy.size === 0) {
    return true;
  }

  return punchQuery.createdBy.has(punch.author.email);
}

function filterPunches(
  punches: PunchOrderedMap,
  sheets: SheetMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  punchQuery: PunchQuery,
  projectGroups: List<ProjectGroup>
): PunchOrderedMap {
  return punches
    .filter((punch) => filterSheets(punch, sheets, markups, punchQuery))
    .filter((punch) => filterStatuses(punch, punchQuery))
    .filter((punch) => filterPriorities(punch, punchQuery))
    .filter((punch) => filterAssignees(punch, punchQuery, projectGroups))
    .filter((punch) => filterUnassigned(punch, punchQuery))
    .filter((punch) => filterDisciplines(punch, punchQuery))
    .filter((punch) => filterLocations(punch, punchQuery))
    .filter((punch) => filterCreatedBy(punch, punchQuery))
    .filter((punch) => filterOverdue(punch, punchQuery));
}

/* Sorting Punches */

interface SortHandlers {
  [key: string]: (
    Punches: PunchOrderedMap,
    direction: string,
    projectGroups: List<ProjectGroup>
  ) => PunchOrderedMap;
}

const sortHandlers: SortHandlers = {
  id: (punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const sortedPunches = punches.sortBy((punch): number => {
      return direction === 'ascending' ? punch.number : -punch.number;
    });

    return sortedPunches;
  },

  status: (punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const sortedPunches = punches.sort((punchA, punchB): number => {
      const a = direction === 'ascending' ? punchB.status : punchA.status;
      const b = direction === 'ascending' ? punchA.status : punchB.status;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedPunches;
  },

  assignees: (
    Punches: PunchOrderedMap,
    direction: string,
    projectGroups: List<ProjectGroup>
  ): PunchOrderedMap => {
    const sortedPunches = Punches.sort((punchA, punchB): number => {
      const groupAList = [...punchA.groupAssignees.values()];
      const groupANameList = getGroupNamesFromIds(projectGroups, groupAList);
      const assigneeAList = [...groupANameList, ...punchA.assignees.values()]
        .map((assignee) => assignee.toLowerCase())
        .sort();

      const groupBList = [...punchB.groupAssignees.values()];
      const groupBNameList = getGroupNamesFromIds(projectGroups, groupBList);
      const assigneeBList = [...groupBNameList, ...punchB.assignees.values()]
        .map((assignee) => assignee.toLowerCase())
        .sort();

      const a = direction === 'ascending' ? assigneeAList : assigneeBList;
      const b = direction === 'ascending' ? assigneeBList : assigneeAList;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedPunches;
  },

  priority: (Punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const priorityNumbers: { [key: string]: number } = {
      Hot: 1,
      High: 2,
      Normal: 3,
      Low: 4,
    };
    const sortedPunches = Punches.sortBy((punch): number => {
      const pNumber = priorityNumbers[punch.priority];

      return direction === 'ascending' ? pNumber : -pNumber;
    });

    return sortedPunches;
  },

  duedate: (punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const sortedPunches = punches.sortBy((punch): number => {
      return direction === 'ascending' ? punch.duedate : -punch.duedate;
    });

    return sortedPunches;
  },

  description: (punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const sortedPunches = punches.sort((punchA, punchB): number => {
      const a =
        direction === 'ascending'
          ? punchA.description.toLowerCase()
          : punchB.description.toLowerCase();
      const b =
        direction === 'ascending'
          ? punchB.description.toLowerCase()
          : punchA.description.toLowerCase();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedPunches;
  },

  location: (punches: PunchOrderedMap, direction): PunchOrderedMap => {
    const sortedPunches = punches.sort((punchA, punchB): number => {
      const a =
        direction === 'ascending'
          ? punchA.location.toLowerCase()
          : punchB.location.toLowerCase();
      const b =
        direction === 'ascending'
          ? punchB.location.toLowerCase()
          : punchA.location.toLowerCase();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedPunches;
  },
};

function sortPunches(
  unsortedPunches: PunchOrderedMap,
  punchQuery: PunchQuery,
  projectGroups: List<ProjectGroup> = List<ProjectGroup>()
): PunchOrderedMap {
  const { sortField, sortDirection } = punchQuery;

  if (sortHandlers[sortField]) {
    const sortedPunches = sortHandlers[sortField](
      unsortedPunches,
      sortDirection,
      projectGroups
    );

    return sortedPunches;
  }

  return unsortedPunches;
}

/* Search Punches */

function searchPunches(
  punches: PunchOrderedMap,
  punchQuery: PunchQuery
): PunchOrderedMap {
  const { search } = punchQuery;

  if (search.length === 0) {
    return punches;
  }

  const sanitizedSearch = search.toLowerCase().replaceAll(/[.-\s]+/g, '');

  const results = punches.filter((punch) => {
    // Number (ID)
    if (punch.number.toString().indexOf(search) === 0) {
      return true;
    }

    // Due Date
    if (getShortDateStr(punch.duedate).indexOf(search) === 0) {
      return true;
    }

    // Description
    const sanitizedDescription = punch.description
      .toLowerCase()
      .replaceAll(/[.-\s]+/g, '');

    if (sanitizedDescription.includes(sanitizedSearch)) {
      return true;
    }

    // Location
    const sanitizedLocation = punch.location
      .toLowerCase()
      .replaceAll(/[.-\s]+/g, '');

    return sanitizedLocation.includes(sanitizedSearch);
  });

  return results;
}

export default function queryPunches(
  punches: PunchOrderedMap,
  sheets: SheetMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  punchQuery: PunchQuery,
  projectGroups: List<ProjectGroup>
): PunchOrderedMap {
  const filteredPunches = filterPunches(
    punches,
    sheets,
    markups,
    punchQuery,
    projectGroups
  );

  const searchedPunches = searchPunches(filteredPunches, punchQuery);

  const sortedPunches = sortPunches(searchedPunches, punchQuery, projectGroups);

  return sortedPunches;
}
